<template>
  <div class="stack-height-calculator">
    <h2>{{ $t('marketing.stack_height.title') }}</h2>
    <div
      v-for="fact in factsToDisplay"
      :class="`stack-height__fact stack-height__${fact.slug}`"
      :key="fact.slug"
    >
      <span
        >{{ fact.name }}:

        <HelpCircledIcon
          v-if="explainers[fact.slug]"
          :id="'filter-explainer' + fact.slug"
          class="filter-explainer"
        />
        <popover
          v-if="explainers[fact.slug]"
          :target="'#filter-explainer' + fact.slug"
          :trigger="$device.isMobileOrTablet ? 'outside-click' : 'hover'"
          placement="bottom"
          :auto-placement="false"
          custom-class="rr_popover filter-explainer-popover"
        >
          <template #popover>
            <span v-html="explainers[fact.slug]" />
          </template>
        </popover>
      </span>
      <div class="stack-height__fact-options">
        <div
          v-for="option in fact.options"
          :key="option.value"
          class="stack-height__fact-option btn"
          @click="select(fact, option)"
          :class="{ selected: isSelected(fact, option) }"
        >
          {{ option.name }}
        </div>
      </div>
    </div>
    <div class="text-center">
      <CtrButton
        class="search"
        @click.native="search"
        :label="searching ? $t('marketing.stack_height.searching') : $t('marketing.stack_height.search')"
        :arrow="false"
        :link="false"
      />
    </div>

    <div class="text-center error-msg-container">
      <span v-if="error" class="error-msg">
        <i class="marketing-tool-error-icon" />
        {{ $t('marketing.stack_height.fill_all') }}
      </span>
    </div>

    <div v-if="results && !searching" class="results">
      <div v-if="results.length > 0">
        <p>
          {{ stackHeightRangeDisplay.includes('-')
            ? $t('marketing.stack_height.description_range', { result: stackHeightRangeDisplay })
            : $t('marketing.stack_height.description_simple', { result: stackHeightRangeDisplay })
          }}.
        </p>
        {{ $t('marketing.stack_height.top_shoes', { count: results.length }) }}
        <a
          :href="localePath(`/${shoe.slug}`)"
          class="stack-height__current-suggestion-shoe"
          v-for="(shoe, index) in results"
          :key="index"
        >
          <TheImage
            profile="thumb-tn"
            :image="shoe.default_color.image"
            :alt="shoe.name"
          />
          {{ shoe.name }}
        </a>
      </div>
      <div v-else>{{ $t('marketing.stack_height.no_result') }}</div>
    </div>
  </div>
</template>

<script>
import HelpCircledIcon from '~/components/icons/awesome-font/HelpCircledIcon';

export default {
  components: {
    HelpCircledIcon,
  },
  data() {
    return {
      facts: [
        {
          name: this.$t('marketing.stack_height.facts.terrain'),
          slug: 'terrain',
          options: [
            { value: 'road', name: this.$t('marketing.stack_height.facts.road') },
            { value: 'trail', name: this.$t('marketing.stack_height.facts.trail') },
          ],
        },
        {
          name: this.$t('marketing.stack_height.facts.pronation'),
          slug: 'pronation',
          options: [
            { value: 'overpronation', name: this.$t('marketing.stack_height.facts.overpronation') },
            { value: 'neutral-pronation', name: this.$t('marketing.stack_height.facts.neutral') },
            { value: 'severe-overpronation', name: this.$t('marketing.stack_height.facts.supination') },
          ],
        },
        {
          name: this.$t('marketing.stack_height.facts.footstrike'),
          slug: 'footstrike',
          options: [
            { value: 'forefoot', name: this.$t('marketing.stack_height.facts.forefoot') },
            { value: 'Midfoot', name: this.$t('marketing.stack_height.facts.midfoot') },
            { value: 'heel-strike', name: this.$t('marketing.stack_height.facts.heel_strike') },
          ],
        },
        {
          name: this.$t('marketing.stack_height.facts.weight'),
          slug: 'weight',
          options: [
            {
              value: 'lighter',
              name: this.$t('marketing.stack_height.facts.lighter'),
            },
            {
              value: 'average',
              name: this.$t('marketing.stack_height.facts.average'),
            },
            {
              value: 'heavy',
              name: this.$t('marketing.stack_height.facts.heavy'),
            },
          ],
        },
        {
          name: this.$t('marketing.stack_height.facts.underfoot_feel'),
          slug: 'underfoot-feel',
          options: [
            {
              value: 'ground-feel',
              name: this.$t('marketing.stack_height.facts.ground_feel'),
            },
            { value: 'ground-shoe', name: this.$t('marketing.stack_height.facts.ground_shoe') },
            { value: 'shoe-feel-only', name: this.$t('marketing.stack_height.facts.shoe_only') },
          ],
        },
        {
          name: this.$t('marketing.stack_height.facts.pace'),
          slug: 'pace',
          options: [
            { value: 'daily-running', name: this.$t('marketing.stack_height.facts.daily') },
            { value: 'speed-training', name: this.$t('marketing.stack_height.facts.speed') },
            { value: 'competition', name: this.$t('marketing.stack_height.facts.competition') },
          ],
        },
      ],
      searching: false,
      results: null,
      selectedOptions: {
        terrain: null,
      },
      error: false,
      explainers: {
        footstrike: this.$t('marketing.stack_height.footstrike_description', { link: this.localePath({ name: 'guides-slug', params: { slug: this.$t('url.footstrike') } }) }),
        pronation: this.$t('marketing.stack_height.pronation_description', { link: this.localePath({ name: 'guides-slug', params: { slug: this.$t('url.pronation') } }) }),
      },
    };
  },
  computed: {
    params() {
      const stackRagne = this.stackHeightRange();
      let params = {
        heel_height_min: stackRagne?.min ?? 0,
        ...this.selectedOptions,
      };

      if (stackRagne?.max) {
        params.heel_height_max = stackRagne.max;
      }

      if (['competition', 'speed-training'].includes(this.selectedOptions.pace)) {
        delete params.pronation;
      }

      if (this.selectedOptions?.['terrain'] === 'trail') {
        delete params.pace;
      }

      return params;
    },
    factsToDisplay() {
      return this.facts.filter(
        fact => !(fact.slug === 'pace' && this.selectedOptions?.['terrain'] === 'trail'),
      );
    },
    stackHeightRangeDisplay() {
      const range = Object.values(this.stackHeightRange());
      return range.length > 1
        ? `${Object.values(this.stackHeightRange()).join('-')}mm `
        : ` ${range[0]}mm and higher`;
    },
  },
  methods: {
    async search() {
      if (!this.validate()) {
        return;
      }
      this.searching = true;
      this.results = await $api(`/stackheight-recommendation`, {
        params: this.params,
      });
      this.searching = false;
    },
    select(fact, option) {
      this.selectedOptions[fact.slug] = option.value;
    },
    isSelected(fact, option) {
      return this.selectedOptions?.[fact.slug] === option.value;
    },
    stackHeightRange() {
      if (
        (this.selectedOptions?.['weight'] === 'heavy' ||
          this.selectedOptions?.['footstrike'] === 'heel-strike') &&
        ['ground-feel', 'ground-shoe'].includes(this.selectedOptions?.['underfoot-feel'] ?? '')
      ) {
        return { min: 14, max: 29 };
      }

      if (this.selectedOptions?.['underfoot-feel'] === 'ground-feel') {
        return { min: 3, max: 13 };
      }

      if (this.selectedOptions?.['underfoot-feel'] === 'ground-shoe') {
        return { min: 9, max: 29 };
      }

      if (this.selectedOptions?.['underfoot-feel'] === 'shoe-feel-only') {
        return { min: 30 };
      }

      return { min: 5, max: 20 };
    },
    validate() {
      const factsNames = this.factsToDisplay.map(fact => fact.slug);
      if (!factsNames.every(factName => this.selectedOptions?.[factName])) {
        this.error = true;
        return false;
      }
      this.error = false;
      return true;
    },
  },

  watch: {
    selectedOptions() {
      console.log('changed options');
    },
  },
};
</script>

<style lang="scss" scoped>
.stack-height-calculator {
  width: 100%;
  h2 {
    color: #fff;
  }
  h2 {
    font-weight: 700;
    font-size: 2.4rem;
    margin-top: 0;
  }
  padding: 50px 30px 40px;
  background-color: $dark-blue;

  .buy_now_button {
    background-color: #f55310;
    padding: 10px 40px;
    border: 0;
    border-radius: 4px;
    font-weight: 700;
    box-shadow: none;
    margin-top: 30px;
  }

  .stack-height__fact {
    display: flex;
    gap: 30px;
    align-items: center;
    justify-content: space-between;
    margin-top: 30px;
    color: #fff;
    @media (max-width: $break_xs_under) {
      flex-direction: column;
      gap: 10px;
    }
    & > span:first-of-type {
      width: 130px;
      @media (max-width: $break_xs_under) {
        width: 100%;
      }
    }
    .stack-height__fact-options {
      width: calc(100% - 130px);
      @media (max-width: $break_xs_under) {
        width: 100%;
      }
      .stack-height__fact-option {
        padding: 15px 20px;
        background-color: #234951;
        border-radius: 5px;
        &:not(:last-of-type) {
          margin-right: 20px;
        }
        @media (max-width: $break_xs_under) {
          &:not(:last-of-type) {
            margin-right: 8px;
          }
          padding: 15px 5px;
        }
        &:hover,
        &.selected {
          border: 1px solid $orange;
          color: $orange;
        }
      }
    }
  }
  .error-msg-container {
    margin-top: 20px;
    .error-msg {
      color: #ed1c24;
      font-weight: bold;
      .marketing-tool-error-icon {
        position: relative;
        top: 2px;
      }
      &:before {
        background: url('#{$cdn_url}/img/marketing-calculator/error-icon.png');
      }
    }
  }

  .results {
    margin-top: 30px;
    color: #fff;
    .stack-height__current-suggestion-shoe {
      margin-top: 10px;
      padding: 1px 10px;
      display: block;
      padding: 15px 10px;
      border-radius: 4px;
      background-color: #fff;
      &,
      * {
        color: #000;
      }

      & > * span:first-of-type {
        width: 100px;
        display: inline-block;
      }
      .suggestion__shoe-timer-faster {
        span:last-of-type {
          color: $green;
        }
      }
    }
  }
}
</style>
